import type { FunctionalComponent } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { signup, verifyEmail } from '../api/client';
import { APP_STORE_BADGE_IMG, APP_STORE_URL } from '../constants/appStore';

function splitName(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: 'User', lastName: 'AU' };
  if (parts.length === 1) return { firstName: parts[0] || 'User', lastName: 'AU' };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
}

const PASSWORD_REQUIREMENTS = [
  { key: 'length', met: (p: string) => p.length >= 8, label: 'At least 8 characters' },
  { key: 'number', met: (p: string) => /\d/.test(p), label: 'At least one number' },
  { key: 'special', met: (p: string) => /[!@_#$%^&*(),.?":{}|<>]/.test(p), label: 'At least one special character (!@_#$%^&*(),.?":{}|<>)' },
];

function checkPasswordValid(password: string): { valid: boolean; message?: string } {
  if (password.length < 8) return { valid: false, message: 'Password must be at least 8 characters' };
  if (!/\d/.test(password)) return { valid: false, message: 'Password must include a number' };
  if (!/[!@_#$%^&*(),.?":{}|<>]/.test(password)) return { valid: false, message: 'Password must include a special character' };
  return { valid: true };
}

interface RegisterModalProps {
  email: string;
  name: string;
  phone?: string;
  referralCode?: string;
  primaryColor: string;
  onClose: () => void;
  onComplete?: (userReferralCode?: string) => void;
}

type Step = 'form' | 'verify' | 'done' | 'gone';

const OTP_LENGTH = 4;
const RESEND_COUNTDOWN = 30;

export const RegisterModal: FunctionalComponent<RegisterModalProps> = ({
  email,
  name,
  phone,
  referralCode,
  primaryColor,
  onClose,
  onComplete,
}) => {
  const { firstName, lastName: effectiveLastName } = splitName(name);
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<Step>('form');
  const [countdown, setCountdown] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [userReferralCode, setUserReferralCode] = useState<string | undefined>();
  const [showPassword, setShowPassword] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  const heroPhoneImage =
    'https://lh3.googleusercontent.com/aida-public/AB6AXuAt5bW3ZB9gNL-1JXf_i2YCM4f19RIV8t220zem8eM2lIIw85AyqWoPpZ8uzdKTYxBmGbgs3iEhtirPZoQGu-PRFTONEB9A-rBHW9FuBscnmPWCTkKVFFGKNBv_j3bttRbXdqauuG779shB46kKvhfmJAaVvAKv5Gmneyz_6umudDV_H-0Vs4zPTt3ns6IVKXEqQHai8U7sllzcbLvvoJZ-1gL7y8cYYvkndaVGu-DHKXOIHatYYe8y1tpDQq0kUx1dWw9kbHT8Mw';

  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const handleCreateAccount = async (e: Event) => {
    e.preventDefault();
    setError(null);
    const pv = checkPasswordValid(password);
    if (!pv.valid) {
      setError(pv.message ?? 'Invalid password');
      return;
    }
    setSubmitting(true);
    try {
      await signup({
        email,
        firstName: firstName.trim() || 'User',
        lastName: effectiveLastName,
        password,
        event_role: 'attendee',
        phoneNumber: phone || undefined,
        referral_code: referralCode?.trim(),
      });
      setStep('verify');
      setCountdown(RESEND_COUNTDOWN);
      setCanResend(false);
    } catch (err: any) {
      setError(err?.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (step !== 'verify' || countdown <= 0) return;
    const t = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          setCanResend(true);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [step, countdown]);

  const handleResend = async () => {
    if (!canResend) return;
    setError(null);
    setSubmitting(true);
    try {
      await signup({
        email,
        firstName: firstName.trim() || 'User',
        lastName: effectiveLastName,
        password,
        event_role: 'attendee',
        phoneNumber: phone || undefined,
        referral_code: referralCode?.trim(),
        resendToken: true,
      });
      setCountdown(RESEND_COUNTDOWN);
      setCanResend(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to resend code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOtpChange = (idx: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...otp];
    next[idx] = value.slice(-1);
    setOtp(next);
    if (value && idx < OTP_LENGTH - 1) {
      otpRefs.current[idx + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (idx: number, e: KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const pasted = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, OTP_LENGTH);
    const next = [...otp];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    for (let i = pasted.length; i < OTP_LENGTH; i++) next[i] = '';
    setOtp(next);
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    otpRefs.current[focusIdx]?.focus();
  };

  const handleVerifyConfirm = async (e: Event) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== OTP_LENGTH) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await verifyEmail({
        code,
        verificationChannel: 'email',
        email,
        firstName: firstName.trim() || 'User',
        lastName: effectiveLastName,
        password,
        role: 'attendee',
        phoneNumber: phone || undefined,
        referral_code: referralCode?.trim(),
      });
      const userReferralCode = (res as any)?.data?.user?.referral_code;
      setStep('done');
      setUserReferralCode(userReferralCode);
    } catch (err: any) {
      setError(err?.message || 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (step !== 'done') return;
    const t = setTimeout(() => {
      setStep('gone');
      onComplete?.(userReferralCode);
    }, 1500);
    return () => clearTimeout(t);
  }, [step, onComplete, userReferralCode]);

  if (step === 'gone') return null;

  return (
    <div
      class="mw-register-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mw-register-modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        class={[
          'mw-register-modal',
          step === 'form' && 'mw-register-modal--wide',
          mounted && 'mw-register-modal--visible',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{ '--mw-reg-primary': primaryColor, '--mw-primary': primaryColor }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          class="mw-register-modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>

        <div class="mw-register-modal-content">
          {step === 'form' && (
            <form onSubmit={handleCreateAccount} class="mw-register-form-pro">
              <div class="mw-register-grid">
                <div class="mw-register-narrative">
                  <div class="mw-reg-pill" aria-hidden="true">
                    <span class="mw-reg-pill-ico" aria-hidden="true">✦</span>
                    <span>Secure access</span>
                  </div>
                  <h1 id="mw-register-modal-title" class="mw-reg-headline">
                    Better experience in the app.
                  </h1>
                  <p class="mw-reg-body">
                    Enter a password below to create your account, then download the Meeteor app to manage your tickets,
                    refunds, and claim points for this transaction.
                  </p>
                  <div class="mw-reg-bento">
                    <div class="mw-reg-bento-media">
                      <div class="mw-reg-bento-shine" />
                      <img class="mw-reg-bento-phone" src={heroPhoneImage} alt="" loading="lazy" />
                    </div>
                    <div class="mw-reg-bento-app">
                      <div class="mw-reg-app-icon">
                        <img src="/meeteor-logo.png" alt="Meeteor" width={88} height={88} />
                      </div>
                      <p class="mw-reg-bento-app-title">Meeteor</p>
                      <a
                        class="mw-reg-app-store-link"
                        href={APP_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Download Meeteor on the App Store"
                      >
                        <img src={APP_STORE_BADGE_IMG} alt="" width={128} height={43} loading="lazy" />
                      </a>
                    </div>
                  </div>
                </div>

                <div class="mw-reg-form-card">
                  <div class="mw-reg-form-inner">
                    <label class="mw-reg-label-caps" htmlFor="mw-reg-password">
                      Secure password
                    </label>
                    <div class="mw-reg-input-shell">
                      <input
                        id="mw-reg-password"
                        class="mw-reg-password-input"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
                        placeholder="Create a strong password"
                        autoComplete="new-password"
                        required
                        disabled={submitting}
                      />
                      <button
                        type="button"
                        class="mw-reg-visibility"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-pressed={showPassword}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        <svg
                          class="mw-reg-visibility-ico"
                          viewBox="0 0 24 24"
                          width="22"
                          height="22"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                        </svg>
                      </button>
                    </div>
                    <div class="mw-reg-req-card">
                      <h4 class="mw-reg-req-title">Password requirements</h4>
                      <ul class="mw-reg-req-list">
                        {PASSWORD_REQUIREMENTS.map((r) => (
                          <li key={r.key} class={r.met(password) ? 'mw-reg-req-item mw-reg-req-item--met' : 'mw-reg-req-item'}>
                            <span class="mw-reg-req-ico" aria-hidden="true">
                              {r.met(password) ? '✓' : ''}
                            </span>
                            {r.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {error && <p class="mw-register-block-error">{error}</p>}
                    <button
                      type="submit"
                      class="mw-reg-submit-btn"
                      disabled={submitting}
                      style={{ backgroundColor: primaryColor }}
                    >
                      {submitting ? (
                        'Sending code…'
                      ) : (
                        <>
                          <span>Create account</span>
                          <svg class="mw-reg-submit-arrow" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" transform="scale(-1,1) translate(-24,0)" />
                          </svg>
                        </>
                      )}
                    </button>
                    <p class="mw-reg-login-hint">
                      Already have an account?{' '}
                      <a class="mw-reg-login-link" href="https://meeteor.me" target="_blank" rel="noopener noreferrer">
                        Open Meeteor
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </form>
          )}

          {step === 'verify' && (
            <form onSubmit={handleVerifyConfirm} class="mw-register-block-form">
              <p class="mw-register-block-verify-hint">
                We've sent a 4-digit verification code to <strong>{email}</strong>
              </p>
              <div class="mw-register-block-otp">
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => { otpRefs.current[idx] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    class="mw-register-block-otp-input"
                    value={digit}
                    onInput={(e) => handleOtpChange(idx, (e.target as HTMLInputElement).value)}
                    onKeyDown={(e) => handleOtpKeyDown(idx, e as unknown as KeyboardEvent)}
                    onPaste={handleOtpPaste}
                    disabled={submitting}
                  />
                ))}
              </div>
              <p class="mw-register-block-resend">
                Didn't receive the code?{' '}
                {canResend ? (
                  <button type="button" class="mw-register-block-resend-btn" onClick={handleResend} disabled={submitting}>
                    Resend
                  </button>
                ) : (
                  <span class="mw-register-block-resend-countdown">Resend ({countdown}s)</span>
                )}
              </p>
              {error && <p class="mw-register-block-error">{error}</p>}
              <button
                type="submit"
                class="mw-register-block-btn"
                disabled={otp.join('').length !== OTP_LENGTH || submitting}
                style={{ backgroundColor: primaryColor }}
              >
                {submitting ? 'Verifying…' : 'Confirm'}
              </button>
            </form>
          )}

          {step === 'done' && (
            <div class="mw-register-block-done">
              <div class="mw-register-block-check">✓</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
